/*
 * smooth-fixes.js — Valour Fits
 * 1) Variant switching: update only the gallery slide(s) that changed instead of
 *    replacing the whole media-gallery (which re-initialised Swiper, reloaded 28 images
 *    and caused a visible flash + layout shift).
 * 2) Keep the add-to-cart button visually stable while a variant request is in flight.
 * 3) Navigation: compute header heights as soon as the DOM is ready (not after every
 *    image has loaded) and keep them in sync on resize.
 */
(function () {
  'use strict';

  /* ---------- 1 & 2: product-info patches ---------- */
  function patchProductInfo() {
    var ProductInfo = customElements.get('product-info');
    if (!ProductInfo || ProductInfo.prototype.__smoothPatched) return;
    var P = ProductInfo.prototype;
    P.__smoothPatched = true;

    var origUpdateMedia = P.updateMedia;
    var origRender = P.renderProductInfo;

    function mediaIdOf(slide) {
      var el = slide && slide.querySelector('[data-media-id]');
      return el ? el.dataset.mediaId : null;
    }

    function preload(img) {
      if (!img) return Promise.resolve();
      var im = new Image();
      if (img.sizes) im.sizes = img.sizes;
      if (img.srcset) im.srcset = img.srcset;
      im.src = img.currentSrc || img.src;
      var decoded = im.decode ? im.decode().catch(function () {}) : Promise.resolve();
      return Promise.race([decoded, new Promise(function (r) { setTimeout(r, 800); })]);
    }

    function toArray(list) { return Array.prototype.slice.call(list); }

    // Clear any half-finished transition so arrows / swipes respond immediately.
    function resetSwiper(sw) {
      sw.animating = false;
      if (sw.wrapperEl) sw.wrapperEl.style.transitionDuration = '0ms';
      sw.update();
    }

    // Keep the (closed) zoom dialog in step with the new colour's images.
    function syncZoomDialog(current, next) {
      var cz = current.querySelector('zoom-dialog');
      var nz = next.querySelector('zoom-dialog');
      if (cz && nz && !cz.querySelector('dialog[open]')) cz.replaceWith(document.importNode(nz, true));
    }

    P.swapGalleryInPlace = function (current, next) {
      var main = current.querySelector('.swiper-main');
      var swiper = main && main.swiper;
      var nextMain = next.querySelector('.swiper-main');
      if (!swiper || !nextMain) return Promise.resolve(false);

      var slideSel = ':scope > .swiper-wrapper > .swiper-slide';
      var curSlides = toArray(main.querySelectorAll(slideSel));
      var newSlides = toArray(nextMain.querySelectorAll(slideSel));
      if (!newSlides.length) return Promise.resolve(false);

      var thumbs = swiper.thumbs && swiper.thumbs.swiper;
      var curThumbs = thumbs ? toArray(thumbs.el.querySelectorAll('.swiper-slide')) : [];
      var newThumbs = toArray(
        next.querySelectorAll('.swiper-thumbs .swiper-slide, .swiper-controls__thumbnails-container .swiper-slide')
      );

      var byIndex = function (i) {
        return curSlides.find(function (s) { return Number(s.dataset.swiperSlideIndex) === i; }) || curSlides[i];
      };
      var sameCount = curSlides.length === newSlides.length && (!thumbs || curThumbs.length === newThumbs.length);

      var changes = [];
      if (sameCount) {
        newSlides.forEach(function (ns, i) {
          var cs = byIndex(i);
          if (mediaIdOf(cs) !== mediaIdOf(ns)) changes.push([cs, ns, curThumbs[i], newThumbs[i]]);
        });
        if (!changes.length) {
          swiper.slideToLoop(0, swiper.params.speed);
          return Promise.resolve(true);
        }
      }

      // Only the first image is visible straight away: wait for it, the rest load lazily.
      return preload(newSlides[0].querySelector('img')).then(function () {
        return new Promise(function (resolve) {
          // Fade the outer Swiper containers, never .swiper-wrapper: Swiper animates the
          // wrapper's transform and waits for its transitionend, so touching the wrapper's
          // transition leaves Swiper "animating" forever (arrows stop working).
          var fadeEls = [main, thumbs && thumbs.el].filter(Boolean);
          fadeEls.forEach(function (el) { el.style.transition = 'opacity .15s ease'; el.style.opacity = '0'; });
          var fadeIn = function () {
            fadeEls.forEach(function (el) { el.style.opacity = '1'; });
            setTimeout(function () {
              fadeEls.forEach(function (el) { el.style.removeProperty('transition'); el.style.removeProperty('opacity'); });
            }, 200);
          };

          setTimeout(function () {
            try {
              if (sameCount) {
                changes.forEach(function (c) {
                  var cs = c[0], ns = c[1], ct = c[2], nt = c[3];
                  var inner = cs.querySelector('.slideshow__slide');
                  var nInner = ns.querySelector('.slideshow__slide');
                  if (inner && nInner) inner.replaceChildren.apply(inner, toArray(nInner.cloneNode(true).childNodes));
                  else cs.innerHTML = ns.innerHTML;
                  if (ct && nt) ct.replaceChildren.apply(ct, toArray(nt.cloneNode(true).childNodes));
                });
              } else {
                // Different number of images for this colour: rebuild the slides through
                // the existing Swiper instance instead of re-creating the whole gallery.
                swiper.removeAllSlides();
                swiper.appendSlide(newSlides.map(function (s) { return document.importNode(s, true); }));
                if (thumbs) {
                  thumbs.removeAllSlides();
                  thumbs.appendSlide(newThumbs.map(function (s) { return document.importNode(s, true); }));
                  thumbs.update();
                }
                swiper.update();
              }
              resetSwiper(swiper);
              if (thumbs) resetSwiper(thumbs);
              swiper.slideToLoop(0, 0);
              if (thumbs) thumbs.slideTo(0, 0);
              syncZoomDialog(current, next);
              requestAnimationFrame(fadeIn);
              resolve(true);
            } catch (e) {
              fadeIn();
              console.warn('[smooth-fixes] gallery swap fell back:', e);
              resolve(false);
            }
          }, 150);
        });
      });
    };

    P.updateMedia = function (html, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;
      var current = this.querySelector('media-gallery');
      var next = html.querySelector('media-gallery');

      if (current && next && current.querySelector('swiper-component')) {
        if (this._lastFeaturedMediaId === variantFeaturedMediaId) return;
        this._lastFeaturedMediaId = variantFeaturedMediaId;
        this.swapGalleryInPlace(current, next).then(function (ok) {
          if (ok) return;
          // Fallback: structure changed – replace, but hold the height so the page doesn't jump.
          current.style.minHeight = current.offsetHeight + 'px';
          current.replaceWith(next);
        });
        return;
      }
      return origUpdateMedia.call(this, html, variantFeaturedMediaId);
    };

    P.renderProductInfo = function (args) {
      var self = this;
      var cb = args.callback;
      self.setAttribute('aria-busy', 'true');
      clearTimeout(self.__busyTimer);
      self.__busyTimer = setTimeout(function () { self.removeAttribute('aria-busy'); }, 8000);
      args.callback = function (html) {
        try { cb(html); } finally {
          clearTimeout(self.__busyTimer);
          self.removeAttribute('aria-busy');
        }
      };
      return origRender.call(this, args);
    };
  }

  if (customElements.get('product-info')) patchProductInfo();
  else customElements.whenDefined('product-info').then(patchProductInfo);

  /* ---------- 4: collection grid ----------
     Only cards appended by infinite scrolling should fade in. Server-rendered cards
     (and cards re-rendered by filters / sorting) already carry data-page, so tag the
     appended ones here. MutationObserver callbacks run before the next paint, so the
     class is in place before the card is first drawn (no flicker). */
  var gridPage = new WeakMap();
  function initialPage(grid) {
    if (!gridPage.has(grid)) {
      var first = grid.querySelector('.product-grid__item[data-page]');
      gridPage.set(grid, grid.dataset.currentPage || (first && first.dataset.page) || '1');
    }
    return gridPage.get(grid);
  }
  function watchGrids() {
    document.querySelectorAll('.product-grid').forEach(initialPage);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList.contains('product-grid')) { initialPage(node); return; }
          if (!node.classList.contains('product-grid__item')) return;
          var grid = node.parentElement;
          if (!grid || !grid.classList.contains('product-grid')) return;
          if (node.dataset.page && node.dataset.page !== initialPage(grid)) node.classList.add('is-appended');
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchGrids);
  else watchGrids();

  /* ---------- 5: collection grid view transitions ----------
     global.js (<grid-view>) listens to window "resize" and, on EVERY resize, calls
     document.startViewTransition() with a view-transition-name on every product card,
     even when the column count hasn't changed. iPhone Safari fires "resize" each time the
     address bar shrinks or grows while you scroll, so the whole page was being frozen
     into snapshots and cross-faded mid-scroll: doubled header, photos drawn over the
     header, flashing. Only animate when the layout really changes. */
  function patchGridView() {
    var GridView = customElements.get('grid-view');
    if (!GridView || GridView.prototype.__smoothPatched) return;
    var P = GridView.prototype;
    P.__smoothPatched = true;
    var origTransition = P.transitionGrid;
    P.transitionGrid = function (gridView, cards) {
      var grid = this.productGrid;
      if (!grid) return;
      if (grid.getAttribute('data-view') === String(gridView)) return;
      return origTransition.call(this, gridView, cards);
    };
  }
  if (customElements.get('grid-view')) patchGridView();
  else customElements.whenDefined('grid-view').then(patchGridView);

  /* ---------- 6: colour swatches on product cards ----------
     The theme's ColorSwatch.handleSwatchClick updates the title, then the price, then the
     photo. For products with no compare-at price the price step throws, so the photo and
     the product link were never updated; for discounted products it swaps in one fixed
     800px photo straight away (a hard cut). Here the photo change is handled properly:
       - the colour photos are fetched ahead of time (desktop: when the pointer enters the
         card; touch screens: when the card is on screen, plus on touch-start), so the
         switch is instant instead of waiting for the network after the tap;
       - the new photo cross-fades in over the old one (no blank frame, no hard cut);
       - the card links point at the chosen variant. */
  var SWATCH_WIDTHS = [360, 533, 720, 940, 1066];
  var swatchCache = {};
  function swatchWidthUrl(url, w) {
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + w);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function swatchSources(url) {
    return {
      srcset: SWATCH_WIDTHS.map(function (w) { return swatchWidthUrl(url, w) + ' ' + w + 'w'; }).join(', '),
      src: swatchWidthUrl(url, 720)
    };
  }
  function cardImg(card) { return card.querySelector('.card__media img'); }
  // Size hint = the card photo's real on-screen width, so the browser picks the right file
  // (the theme's own sizes attribute is malformed and falls back to full screen width).
  function cardSizes(card) {
    var img = cardImg(card);
    var w = img ? Math.round(img.getBoundingClientRect().width) : 0;
    return w > 0 ? w + 'px' : '';
  }
  // Fetch + decode one colour photo once; later calls reuse the same promise.
  function preloadSwatch(swatch, card) {
    var url = swatch && swatch.getAttribute('data-variant-img');
    if (!url) return Promise.resolve();
    var key = url.split('?')[0];
    if (swatchCache[key]) return swatchCache[key];
    var srcs = swatchSources(url);
    var im = new Image();
    im.decoding = 'async';
    var sizes = cardSizes(card);
    if (sizes) im.sizes = sizes;
    im.srcset = srcs.srcset;
    im.src = srcs.src;
    swatchCache[key] = (im.decode ? im.decode() : new Promise(function (r) { im.onload = r; im.onerror = r; }))
      .catch(function () {});
    return swatchCache[key];
  }
  function preloadCard(card) {
    if (!card || card.__swatchPreloaded) return;
    card.__swatchPreloaded = true;
    card.querySelectorAll('color-swatch [data-variant-img]').forEach(function (sw) { preloadSwatch(sw, card); });
  }

  var touchScreen = window.matchMedia && window.matchMedia('(hover: none)').matches;
  // Desktop: the pointer crosses the card before it reaches a swatch.
  document.addEventListener('pointerover', function (e) {
    var card = e.target.closest && e.target.closest('.card-wrapper');
    if (card && card.querySelector('color-swatch')) preloadCard(card);
  }, { passive: true });
  // Touch: start fetching the moment a swatch is touched (before the click arrives)...
  document.addEventListener('touchstart', function (e) {
    var sw = e.target.closest && e.target.closest('color-swatch [data-variant-img]');
    if (sw) preloadSwatch(sw, sw.closest('.card-wrapper'));
  }, { passive: true });
  // ...and for cards that stay on screen for a moment, fetch their colours in idle time.
  if (touchScreen && 'IntersectionObserver' in window) {
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 200); };
    var timers = new WeakMap();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var card = en.target;
        if (en.isIntersecting) {
          timers.set(card, setTimeout(function () { idle(function () { preloadCard(card); }); io.unobserve(card); }, 400));
        } else {
          clearTimeout(timers.get(card));
        }
      });
    }, { threshold: 0.6 });
    var watchCards = function (root) {
      (root.querySelectorAll ? root : document).querySelectorAll('.card-wrapper').forEach(function (c) {
        if (!c.__swatchWatched && c.querySelector('color-swatch [data-variant-img]')) { c.__swatchWatched = true; io.observe(c); }
      });
    };
    var startWatching = function () {
      watchCards(document);
      new MutationObserver(function (ms) {
        ms.forEach(function (m) { m.addedNodes.forEach(function (n) { if (n.nodeType === 1) watchCards(n); }); });
      }).observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startWatching);
    else startWatching();
  }

  // Remember the photo as it was before the theme's click handler touches it.
  document.addEventListener('click', function (event) {
    var swatch = event.target && event.target.closest && event.target.closest('color-swatch [data-variant-id]');
    var card = swatch && swatch.closest('.card-wrapper');
    if (!card) return;
    card.__swatchBefore = Array.prototype.map.call(card.querySelectorAll('.card__media img'), function (img) {
      return { img: img, srcset: img.getAttribute('srcset'), src: img.getAttribute('src') };
    });
  }, true);

  // The theme's own click listener runs in between (and may throw); this one runs after
  // it on the document, so it always completes.
  document.addEventListener('click', function (event) {
    var swatch = event.target && event.target.closest && event.target.closest('color-swatch [data-variant-id]');
    var card = swatch && swatch.closest('.card-wrapper');
    if (!card) return;

    // Undo the theme's instant single-image swap (same task, so it is never painted).
    (card.__swatchBefore || []).forEach(function (b) {
      if (b.img.getAttribute('srcset') !== b.srcset) {
        if (b.srcset == null) b.img.removeAttribute('srcset'); else b.img.setAttribute('srcset', b.srcset);
      }
      if (b.img.getAttribute('src') !== b.src && b.src != null) b.img.setAttribute('src', b.src);
    });

    var variantId = swatch.getAttribute('data-variant-id');
    card.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var href = a.getAttribute('href').split('?')[0];
      a.setAttribute('href', href + '?variant=' + variantId);
    });

    var url = swatch.getAttribute('data-variant-img');
    var imgs = card.querySelectorAll('.card__media img');
    var first = imgs[0];
    if (!url || !first) return;
    if ((first.currentSrc || first.src).split('?')[0].split('/').pop() === url.split('?')[0].split('/').pop()) return;

    var srcs = swatchSources(url);
    var sizes = cardSizes(card);
    var token = (card.__swatchToken = (card.__swatchToken || 0) + 1);
    var timeout = new Promise(function (r) { setTimeout(r, 4000); });

    Promise.race([preloadSwatch(swatch, card), timeout]).then(function () {
      if (card.__swatchToken !== token) return; // a newer swatch was tapped meanwhile
      var media = first.parentElement;
      var cs = getComputedStyle(first);

      // Cross-fade: lay the new photo over the old one, fade it in, then commit it.
      var layer = document.createElement('div');
      layer.setAttribute('aria-hidden', 'true');
      layer.style.cssText = 'position:absolute;inset:0;z-index:1;pointer-events:none;opacity:0;transition:opacity .25s ease;';
      var pic = document.createElement('img');
      pic.alt = '';
      pic.decoding = 'sync';
      if (sizes) pic.sizes = sizes;
      pic.srcset = srcs.srcset;
      pic.src = srcs.src;
      pic.style.cssText = 'display:block;width:100%;height:100%;object-fit:' + cs.objectFit + ';object-position:' + cs.objectPosition + ';';
      layer.appendChild(pic);
      if (card.__swatchLayer) card.__swatchLayer.remove();
      card.__swatchLayer = layer;
      media.appendChild(layer);

      var commit = function () {
        if (card.__swatchToken !== token) return;
        Array.prototype.forEach.call(imgs, function (img) {
          img.loading = 'eager';
          if (sizes) img.sizes = sizes;
          img.srcset = srcs.srcset;
          img.src = srcs.src;
        });
        var done = function () {
          if (card.__swatchLayer === layer) { layer.remove(); card.__swatchLayer = null; }
        };
        (first.decode ? first.decode() : Promise.resolve()).catch(function () {}).then(done);
        setTimeout(done, 1500);
      };
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          layer.style.opacity = '1';
          setTimeout(commit, 260);
        });
      });
    });
  });

  /* ---------- 3: header heights ---------- */
  function syncHeader() {
    if (typeof updateHeaderHeights === 'function') updateHeaderHeights();
    if (typeof setheaderRowHeight === 'function') setheaderRowHeight();
  }
  function initHeader() {
    syncHeader();
    var group = document.getElementById('header-group');
    if (group && 'ResizeObserver' in window) {
      var raf;
      var ro = new ResizeObserver(function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(syncHeader);
      });
      // #header-group uses display: contents, so observe its children.
      Array.prototype.forEach.call(group.children, function (el) { ro.observe(el); });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHeader);
  else initHeader();
})();
